import { Component, NgZone, Optional, ViewChild } from '@angular/core';
import { GlobalesService } from './services/globales.service';
import MenuModel from './model/menu/menuModel';
import { MainService } from './api/main.service';
import { LoginControlComponent } from './components/login-control/login-control.component';
import { LoaderComponent } from './components/loader/loader.component';
import { IonMenu, NavController, IonModal } from '@ionic/angular/standalone';
import { IonicComponentsModule } from './modules/ionic-components.module';
import { MenuService } from './services/menu.service';
import { register } from 'swiper/element/bundle'
import { AlertService } from './services/alert.service';
import { BehaviorSubject, filter, firstValueFrom, interval, Subject, take, takeUntil, timer } from 'rxjs';
import { VersionesService } from './services/versiones.service';
import { SwUpdate } from '@angular/service-worker';
import { DatabaseService } from './database/database.service';
import { InspeccionesService } from './services/inspecciones.service';
import { PendingTasksService } from './services/pending-tasks.service';
import ParametrosClass from './model/api/inspecciones/parametrosClass';
import { InspeccionFormsService } from './services/inspeccion-forms.service';
import { LogicData } from './model/api/inspecciones/inspeccionLogic';
import DefectoClass from './model/api/inspecciones/defectoClass';

register()

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    IonicComponentsModule,
    LoginControlComponent,
    LoaderComponent
  ],
})
export class AppComponent {

  //#region Propiedades

  @ViewChild(IonMenu) public menu: IonMenu
  private ShowMessageSubject = new Subject<{message: string, title: string, cb: () => void}>()
  private ShowConfirmSubject = new Subject<{message: string, title: string, acceptText: string, cancelText: string, cb: (result: boolean) => void}>()
  private destroy$ = new Subject<void>()
  public changeLog: string = ''
  @ViewChild("mdlChangelog") public mdlChangelog: IonModal

  //#endregion

  //#region Constructor

  constructor(
    public globales: GlobalesService,
    public navCtrl: NavController,
    public service: MainService,
    public menuService: MenuService,
    private alertService: AlertService,
    private zone: NgZone,
    private versiones: VersionesService,
    @Optional() private swUpdate: SwUpdate,
    private database: DatabaseService,
    private inspeccionesService: InspeccionesService,
    private inspeccionForms: InspeccionFormsService,
    private pendingTasks: PendingTasksService
  ) { }

  //#endregion

  //#region Init

  public async ngOnInit(): Promise<void> {
    
    // Exponemos métodos útiles de forma global para que puedan ser utilizados por la lógica de las OTs
    window["showMessage"] = (message: string, title?: string) => {
      if (title == null) title = 'Ingein'
      return new Promise<void>((resolve) => {
        this.ShowMessageSubject.next({
          message: message,
          title: title,
          cb: () => {
            resolve()
          }
        })
      })
    }
    window["showConfirm"] = (message: string, title: string, acceptText?: string, cancelText?: string) => {
      if (title == null) title = 'Ingein'
      if (acceptText == null) acceptText = 'Sí'
      if (cancelText == null) cancelText = 'No'
      return new Promise<boolean>((resolve) => {
        this.ShowConfirmSubject.next({
          message: message,
          title: title,
          acceptText: acceptText,
          cancelText: cancelText,
          cb: (value) => {
            resolve(value)
          }
        })
      })
    }

    window["runTableLogic"] = (tableName: string, logic: (tableLines: Record<string, ParametrosClass>[]) => void) => {
      this.inspeccionForms.runTableLogic.next({
        tableName: tableName,
        logic: logic
      })
    }

    window["runLogicOT"] = (logic: (data: LogicData) => void) => {
      this.inspeccionForms.runLogicOT.next({
        logic: logic
      })
    }

    window["gestionarDefectosParametro"] = (parametro: string, logic: (defectosActivos: DefectoClass[], defectosMaestro: DefectoClass[]) => void) => {
      this.inspeccionForms.gestionarDefectosParametro.next({
        parametro: parametro,
        logic: logic
      })
    }

    window["setCamposCheckDefectos"] = (codigos: string[]) => {
      this.inspeccionForms.setCamposCheckDefectos.next({
        codigos: codigos
      })
    }

    window["debugMode"] = () => {
      this.zone.run(() => {
        const mostrarCodigo = this.globales.mostrarCodigo.value
        this.globales.mostrarCodigo.next(!mostrarCodigo)
      })
    }

    this.ShowMessageSubject.pipe(
      takeUntil(this.destroy$)
    ).subscribe((obj) => {
      this.zone.run(async () => {
        await this.alertService.showMessage(obj.message, obj.title)
        if (obj.cb != null) {
          obj.cb()
        }
      })
    })

    this.ShowConfirmSubject.pipe(
      takeUntil(this.destroy$)
    ).subscribe((obj) => {
      this.zone.run(async () => {
        let result = await this.alertService.showConfirm(obj.message, obj.title, obj.acceptText, obj.cancelText)
        if (obj.cb != null) {
          obj.cb(result)
        }
      })
    })

    this.changeLog = await this.versiones.checkAppUpdated()
    if (this.changeLog != null && this.changeLog != '') {
      this.mdlChangelog.isOpen = true
    }

    // Actualizaciones por Service Worker
    if (this.swUpdate?.isEnabled) {

      // El Service Worker solo comprueba si hay una versión nueva al arrancar la aplicación, así que en una
      // sesión larga (tablet con la app abierta durante horas) forzamos la comprobación cada 5 minutos.
      interval(5 * 60 * 1000).pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.swUpdate.checkForUpdate()
      })

      this.swUpdate.versionUpdates.pipe(
        takeUntil(this.destroy$)
      ).subscribe(async (event) => {
        if (event.type == 'VERSION_DETECTED') {
          console.log(`Se ha detectado una nueva versión.`)
          this.globales.estadoAplicacion.next('Descargando')
        }
        if (event.type == 'VERSION_INSTALLATION_FAILED') {
          console.log(`Ha fallado la actualización por el Service Worker.`)
        }
        if (event.type == 'VERSION_READY') {
          console.log(`Se ha aplicado una nueva actualización. Estará disponible al recargar la página.`)
          this.globales.estadoAplicacion.next('ActualizacionPendiente')

          // Recargamos automáticamente en cuanto no haya ninguna tarea pendiente (descargas, sincronizaciones,
          // edición de una OT, etc.) que se pudiera ver interrumpida por la recarga.
          this.pendingTasks.isBusy.pipe(
            filter(busy => !busy),
            take(1),
            takeUntil(this.destroy$)
          ).subscribe(() => {
            location.reload()
          })
        }
        if (event.type == 'NO_NEW_VERSION_DETECTED') {
          let previousState = await firstValueFrom(this.globales.estadoAplicacion)
          console.log(`No hay actualizaciones disponibles.`)
          if (!(previousState == 'ActualizacionPendiente')) {
            this.globales.estadoAplicacion.next('Actualizada')
          }
        }
      })
    } else {
      this.globales.estadoAplicacion.next('NoServiceWorker')
      console.log(`El service worker no está activado.`)
    }

    this.globales.backupConfigurado.pipe(
      filter(p => p === -1)
    ).subscribe(() => this.checkBackupsEnabled())

  }

  public async checkBackupsEnabled(): Promise<void> {

    // Determinamos el estado de las copias de seguridad.
    const directory = await this.database.Directories.getByPurpose('BackupOTs')

    // Si no existe un directorio configurado, las copias no están configuradas.
    if (directory == null) {
      this.globales.backupConfigurado.next(2)

      this.alertService.showMessage(
        'Las copias de seguridad no están configuradas en esta tablet.'
      )

      return
    }

    // Existe configuración. Comprobamos que el directorio siga siendo accesible
    // y que podamos escribir correctamente en él.
    let canWrite = false
    let errorMessage = ''

    const handle = directory.Handle
    const fileName = `check-${Date.now()}.tmp`

    try {

      // Creamos el fichero temporal.
      const fileHandle = await handle.getFileHandle(fileName, {
        create: true
      })

      // Escribimos contenido en el fichero.
      const writable = await fileHandle.createWritable()
      await writable.write('ok')
      await writable.close()

      // Volvemos a leer el fichero para comprobar que la escritura
      // se ha realizado correctamente.
      const file = await fileHandle.getFile()
      const content = await file.text()

      if (content !== 'ok') {
        throw new Error(
          'El fichero de comprobación se ha creado, pero su contenido no es correcto.'
        )
      }

      canWrite = true

    } catch (ex: any) {

      canWrite = false

      // Intentamos obtener una pista útil del error.
      if (ex?.message) {
        errorMessage = ex.message
      } else if (ex?.name) {
        errorMessage = ex.name
      } else {
        errorMessage = 'No se ha podido acceder o escribir en el directorio configurado.'
      }

    } finally {

      // Intentamos eliminar el fichero temporal si se llegó a crear.
      try {
        await handle.removeEntry(fileName)
      } catch (deleteEx) {
        // No consideramos que falle el backup únicamente porque no podamos
        // eliminar el fichero temporal de comprobación.
      }

    }

    this.globales.backupConfigurado.next(canWrite ? 1 : 2)

    if (!canWrite) {
      this.alertService.showMessage(
        `Las copias de seguridad no están funcionando en esta tablet: ${errorMessage}`
      )
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  //#endregion

  //#region Menú

  public openPage(page: MenuModel): void {
    this.navCtrl.navigateRoot(page.url)
    this.menu.close()
  }

  //#endregion

}
